import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Boxes } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const STATES = [
  "Tamil Nadu", "Kerala", "Karnataka", "Andhra Pradesh", "Telangana", "Puducherry", "Maharashtra", "Goa", "Gujarat",
  "Delhi", "Rajasthan", "Madhya Pradesh", "Uttar Pradesh", "Bihar", "West Bengal", "Odisha", "Jharkhand", "Chhattisgarh",
  "Punjab", "Haryana", "Himachal Pradesh", "Uttarakhand", "Jammu and Kashmir", "Ladakh", "Chandigarh", "Assam",
  "Arunachal Pradesh", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Sikkim", "Tripura",
  "Andaman and Nicobar Islands", "Dadra and Nagar Haveli and Daman and Diu", "Lakshadweep",
];
const MAPS_RE = /^https?:\/\/(maps\.app\.goo\.gl\/|goo\.gl\/maps\/|(www\.)?google\.[a-z]{2,3}(\.[a-z]{2})?\/maps|maps\.google\.[a-z]{2,3}(\.[a-z]{2})?\/)/i;
const cleanPhone = (v) => {
  let d = v.replace(/\D/g, "");
  if (d.length > 10 && d.startsWith("91")) d = d.slice(2);
  else if (d.length > 10 && d.startsWith("0")) d = d.slice(1);
  return d.slice(0, 10);
};

const BUSINESS_TYPES = ["Retailer", "Distributor", "Reseller", "Restaurant / HoReCa", "Manufacturer", "Other"];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    shop_name: "", contact_person: "", email: "", password: "",
    business_type: "Retailer", gstin: "", phone: "", shop_image: "",
    google_location: "", city: "", state: "Tamil Nadu",
  });
  const [imgName, setImgName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => { setError(""); setForm((f) => ({ ...f, [k]: e.target.value })); };

  const onImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => { setForm((f) => ({ ...f, shop_image: reader.result })); setImgName(file.name); };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[6-9]\d{9}$/.test(form.phone)) { setError("Enter a valid 10-digit mobile number (without +91 or 0)."); return; }
    const loc = form.google_location.trim();
    if (loc && !MAPS_RE.test(loc)) { setError("Google Location must be a Google Maps link (maps.app.goo.gl/... or google.com/maps/...)."); return; }
    if (!form.shop_image && !loc) { setError("Please upload a shop photo or paste your shop's Google Maps location link."); return; }
    if (!form.city.trim() || !form.state) { setError("Please enter your city and state."); return; }
    setLoading(true);
    try {
      await register(form);
      toast.success("Registration submitted! Awaiting admin approval.");
      navigate("/account");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-6">
      <div className="max-w-xl mx-auto">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <img src="/logo.png" alt="Zealous Ventures" className="h-14 w-auto" />
        </Link>

        <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8">
          <h1 className="font-heading text-2xl font-bold text-slate-900">Open a wholesale account</h1>
          <p className="text-slate-500 text-sm mt-1 mb-6">
            Tell us about your business. Prices &amp; ordering unlock after admin verification.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Shop name <span className="text-rose-500">*</span></Label>
                <Input data-testid="reg-company" value={form.shop_name} onChange={set("shop_name")} required className="mt-1.5 h-11" />
              </div>
              <div>
                <Label>Contact person <span className="text-rose-500">*</span></Label>
                <Input data-testid="reg-contact" value={form.contact_person} onChange={set("contact_person")} required className="mt-1.5 h-11" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Business type</Label>
                <Select value={form.business_type} onValueChange={(v) => setForm((f) => ({ ...f, business_type: v }))}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="reg-business-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>GSTIN / Tax ID</Label>
                <Input data-testid="reg-gstin" value={form.gstin} onChange={set("gstin")} className="mt-1.5 h-11" placeholder="Optional" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Email <span className="text-rose-500">*</span></Label>
                <Input data-testid="reg-email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5 h-11" placeholder="you@business.com" />
              </div>
              <div>
                <Label>Phone <span className="text-rose-500">*</span></Label>
                <Input data-testid="reg-phone" value={form.phone} inputMode="numeric" maxLength={16}
                  onChange={(e) => { setError(""); const v = cleanPhone(e.target.value); setForm((f) => ({ ...f, phone: v })); }}
                  required className="mt-1.5 h-11 font-mono-num" placeholder="10-digit mobile" />
              </div>
            </div>
            <p className="text-xs text-slate-400 -mt-2">Sign in with your email or phone. Password reset links are sent to your email.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>City <span className="text-rose-500">*</span></Label>
                <Input data-testid="reg-city" value={form.city} onChange={set("city")} required className="mt-1.5 h-11" placeholder="e.g. Tiruppur" />
              </div>
              <div>
                <Label>State <span className="text-rose-500">*</span></Label>
                <Select value={form.state} onValueChange={(v) => setForm((f) => ({ ...f, state: v }))}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="reg-state"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">Add <b>a shop photo or a Google Maps link</b> (at least one) so we can verify your shop.</p>
            <div>
              <Label>Shop Google Location link</Label>
              <Input data-testid="reg-google-location" value={form.google_location} onChange={set("google_location")} className="mt-1.5 h-11" placeholder="https://maps.app.goo.gl/..." />
            </div>
            <div>
              <Label>Shop Visiting Card or Shop Photo</Label>
              <label htmlFor="shop-image" className="mt-1.5 flex items-center gap-3 h-11 px-4 rounded-md border border-dashed border-slate-300 cursor-pointer hover:border-slate-400 text-sm text-slate-500" data-testid="reg-shop-image-label">
                <span className="font-medium text-slate-700">Upload image</span>
                <span className="truncate">{imgName || "PNG/JPG, max 5 MB"}</span>
              </label>
              <input id="shop-image" type="file" accept="image/*" onChange={onImage} className="hidden" data-testid="reg-shop-image" />
              {form.shop_image && (
                <img src={form.shop_image} alt="Shop preview" className="mt-3 h-28 rounded-lg border border-slate-200 object-cover" data-testid="reg-shop-image-preview" />
              )}
            </div>
            <div>
              <Label>Password <span className="text-rose-500">*</span></Label>
              <PasswordInput data-testid="reg-password" value={form.password} onChange={set("password")} required minLength={6} wrapperClassName="mt-1.5" className="h-11" placeholder="Minimum 6 characters" />
            </div>
            {error && <div className="text-sm text-rose-600" data-testid="register-error">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-slate-900 hover:bg-black text-white" data-testid="register-submit">
              {loading ? "Submitting..." : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-slate-500 mt-6 text-center">
            Already registered?{" "}
            <Link to="/login" className="text-amber-600 font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
